import { apiService } from '@/services/api/apiService';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

interface AreaManager {
    firstName: any,
    lastName: any,
    dateOfBirth: any,
    genderId: any,
    phone: any,
    email: any,
    profileImageUrl: any,
    auditTrailId: any,
    isActive: boolean
    id: any
    cuisines: Cuisine[];
}
export interface Cuisine {
    cuisineTypeId: number;
    cuisineTypeName: string;
}
interface AreaItem {
    id: number;
    areaCode: string;
    isActive: boolean;
    areaCodeId: any;
    areaCodeName: string;
    auditTrailId: number | null;
    userProfileId: number;
}

interface AreaManagersState {
    data: AreaManager[];
    getByIdData: AreaManager | null;
    getByIdAreaCoverdByAreaManager: AreaItem[];
    getByIdManagerCookByAreaCode: AreaItem[];
    getByAreaCodeIdCustomerOrderData: any;
    loading: boolean;
    error: any;
}

const PORT = import.meta.env.VITE_COOK_PORT;
const URL = import.meta.env.VITE_API_COOK_URL;

const FOODORDERPORT = import.meta.env.VITE_FOOD_ORDER_PORT;
const FOODORDERURL = import.meta.env.VITE_API_FOOD_ORDER_URL;

const AREAMANAGERPORT = import.meta.env.VITE_USERS_PORT;
const AREAMANAGERURL = import.meta.env.VITE_API_USERS_URL;

export const fetchAreaManager = createAsyncThunk<AreaManager[], void, { rejectValue: string }>(
    'AreaManagers/fetchAreaManager',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.get<{ data: AreaManager[] }>('/get_all_area_manager', AREAMANAGERPORT, AREAMANAGERURL);
            return response.data;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
        }
    }
);

export const getByIdAreaManager = createAsyncThunk<
    AreaManager,
    { id: any | string },
    { rejectValue: string }
>(
    'AreaManagers/getByIdAreaManager',
    async (params, { rejectWithValue }) => {
        try {
            const config = { params }
            const response = await apiService.get<{ data: AreaManager }>(
                '/get_by_id_area_manager',
                AREAMANAGERPORT,
                AREAMANAGERURL,
                config
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to fetch area manager');
        }
    }
);

export const areaCoverdByAreaManager = createAsyncThunk<
    AreaItem[],
    { areaManagerId: any | string },
    { rejectValue: string }
>(
    'AreaManagers/areaCoverdByAreaManager',
    async (params, { rejectWithValue }) => {
        try {
            const config = { params }
            const response = await apiService.get<{ data: AreaItem[] }>(
                '/get_all_area_covered_by_area_manager',
                PORT,
                URL,
                config
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to fetch areas covered');
        }
    }
);

export const cookByAreaCode = createAsyncThunk<
    AreaItem[],
    { areaCodeId?: any },
    { rejectValue: string }
>(
    "AreaManagers/cookByAreaCode",
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.post<{ data: AreaItem[] }>(
                "/get_all_cook_by_area_code_id",
                params,
                AREAMANAGERPORT,
                AREAMANAGERURL
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);


export const createAreaManager = createAsyncThunk<AreaManager, any, { rejectValue: string }>(
    'AreaManagers/createAreaManager',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.post<{ data: AreaManager }>('create_area_manager', params, PORT, URL);
            return response.data;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
        }
    }
);

export const updateAreaManager = createAsyncThunk<AreaManager, any, { rejectValue: string }>(
    'AreaManagers/updateAreaManager',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.put<{ data: AreaManager }>('update_areamanager', params, AREAMANAGERPORT, AREAMANAGERURL);
            return response.data;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
        }
    }
);


export const getByAreaCodeIdCustomerOrder = createAsyncThunk<
    AreaItem[],
    {
        areaCodeId: number | string | number[],
        areaManagerUserId?: number
    },
    { rejectValue: string }
>(
    'AreaManagers/getByAreaCodeIdCustomerOrder',
    async ({ areaCodeId, areaManagerUserId }, { rejectWithValue }) => {
        try {
            const paramValue = Array.isArray(areaCodeId)
                ? areaCodeId.join(',')
                : areaCodeId;
            const params: Record<string, any> = { areaCodeId: paramValue };

            if (areaManagerUserId) {
                params.areaManagerUserId = areaManagerUserId;
            }
            const config = { params };

            const response = await apiService.get<{ data: AreaItem[] }>(
                '/get_by_area_code_id_customer_order',
                FOODORDERPORT,
                FOODORDERURL,
                config
            );

            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error?.message || 'Failed to fetch cooks by area code'
            );
        }
    }
);


export const AreaManagerSlice = createSlice({
    name: 'AreaManager',
    initialState: {
        data: [],
        getByIdData: null,
        getByIdAreaCoverdByAreaManager: [],
        getByIdManagerCookByAreaCode: [],
        getByAreaCodeIdCustomerOrderData: null,
        loading: false,
        error: null,
    } as unknown as AreaManagersState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchAreaManager.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAreaManager.fulfilled, (state, action: PayloadAction<AreaManager[]>) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchAreaManager.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? 'Unknown error';
            })

            .addCase(getByIdAreaManager.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getByIdAreaManager.fulfilled, (state, action: PayloadAction<AreaManager>) => {
                state.loading = false;
                state.getByIdData = action.payload;
            })
            .addCase(getByIdAreaManager.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? 'Unknown error';
            })

            .addCase(areaCoverdByAreaManager.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(areaCoverdByAreaManager.fulfilled, (state, action: PayloadAction<AreaItem[]>) => {
                state.loading = false;
                state.getByIdAreaCoverdByAreaManager = action.payload;
            })
            .addCase(areaCoverdByAreaManager.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? 'Unknown error';
            })

            .addCase(cookByAreaCode.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(cookByAreaCode.fulfilled, (state, action: PayloadAction<AreaItem[]>) => {
                state.loading = false;
                state.getByIdManagerCookByAreaCode = action.payload;
            })
            .addCase(cookByAreaCode.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? 'Unknown error';
            })
            .addCase(getByAreaCodeIdCustomerOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getByAreaCodeIdCustomerOrder.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.getByAreaCodeIdCustomerOrderData = action.payload;
            })
            .addCase(getByAreaCodeIdCustomerOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? 'Unknown error';
            })
    },
});

export default AreaManagerSlice.reducer;